import { FC, KeyboardEvent, useContext, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Badge,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Drawer,
  IconButton,
  InputAdornment,
  LinearProgress,
  Snackbar,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { useNavigate, useParams } from 'react-router';
import { useMutation, useQueryClient } from 'react-query';
import SearchIcon from '@mui/icons-material/Search';
import FilterListIcon from '@mui/icons-material/FilterList';
import CloseIcon from '@mui/icons-material/Close';
import Container from '../../components/Container';
import Input from '../../components/Input';
import ListPagination, {
  DEFAULT_PAGE_SIZE,
} from '../../components/ListPagination';
import { FIELD_SX } from '../../components/fieldStyles';
import { UserContext } from '../..';
import { archivosFetch } from '../../utils/fetch';
import { BRAND_DIALOG_CLASS } from '../../theme';
import { colors, fontWeights, radii } from '../../theme/tokens';
import { FilterOption } from '../Home/types';
import { HOME_USERS_KEY, useFilterOptions } from '../Home/useFilterOptions';
import UserFormDialog, { UserFormMode, UserFormNotice } from '../CreateUser';
import { UserCards, UsersTable, UserResolvers } from './UsersTable';
import UserFilterFields, { ROLE_OPTIONS } from './UserFilterFields';
import { UserAction, displayName } from './UserRowActions';
import { useUsers } from './useUsers';
import { EMPTY_USER_FILTERS, UserFilters, UserRow, formatLastLogin } from './types';

// XD screen 8 ("8. Liste users" in DESIGN.md): header and tiles
// (Container), filter row ("Qui cherchez-vous ?", Services, Rôle) and the
// users table with the Home pattern.
//
// Screen 9 ("Add user") is a modal over this one: `/create-user` mounts
// this same component with `dialog="create"`, and closing the modal returns to
// `/users-list`. That way the `/create-user` route still exists (guards, tile,
// specs) even though it is the list with the modal open. `/edit-user/:id` does the
// same with `dialog="edit"`, and `/view-user/:id` (the name link in the
// table) with `dialog="view"`: the same modal read-only, with "Modifier".
//
// Decisions where the XD or the API give no data:
//   - "Services" and "Rôle" filters: the API does not have them; see useUsers for
//     how they are applied without misleading with the pagination.
//   - "Dernière connexion": `last_login_at`, null until the first login.
//   - Deletion: not offered on one's own account. The backend allows it,
//     but deleting oneself with the session open leaves the app in a
//     dead-end state.
//   - The "Status" column of the previous listing (icon for `forbidden`) is not
//     in the XD and is removed.
//   - Empty/loading/error states (gap 8): in UsersTable, via ListTable.
//   - Mobile (ROADMAP.md, Part 2): full-width search box + "Filtres" that
//     opens a bottom sheet; applied filters as chips; cards.

type Props = {
  /** Modal open over the list, according to the route. */
  dialog?: UserFormMode;
};

type Notice = {
  severity: 'success' | 'warning' | 'error';
  text: string;
};

const GENERIC_ERROR =
  "L'opération n'a pas pu aboutir. Veuillez réessayer dans quelques instants.";

const UsersList: FC<Props> = ({ dialog }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('tablet'));
  const navigate = useNavigate();
  const { id: editedId } = useParams();
  const queryClient = useQueryClient();
  const userContext = useContext(UserContext);
  const myId = userContext?.mainUser?.id ?? '';

  const [filters, setFilters] = useState<UserFilters>(EMPTY_USER_FILTERS);
  const [searchDraft, setSearchDraft] = useState('');
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [sheetDraft, setSheetDraft] = useState<UserFilters>(EMPTY_USER_FILTERS);
  const [toBeDeleted, setToBeDeleted] = useState<UserRow>();
  const [notice, setNotice] = useState<Notice>();

  useEffect(() => {
    document.title =
      dialog === 'create'
        ? 'ArchivOs - Ajouter un utilisateur'
        : dialog === 'edit'
        ? 'ArchivOs - Modifier un utilisateur'
        : dialog === 'view'
        ? 'ArchivOs - Voir utilisateur'
        : "ArchivOs - Liste des utilisateurs";
  }, [dialog]);

  const { services } = useFilterOptions(false);
  const { data, isLoading, isFetching, isError, refetch } = useUsers({
    filters,
    page,
    pageSize,
  });

  const users = data?.users ?? [];
  const total = data?.totalItems ?? 0;

  const { mutateAsync: deleteUser, isLoading: isDeleting } = useMutation(
    (id: string) =>
      archivosFetch(`${process.env.REACT_APP_API_URL}/api/users/${id}/delete-user`, {
        method: 'DELETE',
      }),
  );

  // --- Options and name resolution ---------------------------------------

  const serviceOptions = useMemo<FilterOption[]>(
    () => [
      { value: '', label: 'Tous' },
      ...services
        .map((service) => ({ value: service.id, label: service.name }))
        .sort((a, b) => a.label.localeCompare(b.label, 'fr')),
    ],
    [services],
  );

  const resolvers: UserResolvers = {
    serviceNames: (user) => {
      const names = (user.services ?? [])
        .map((serviceId) => services.find((s) => s.id === serviceId)?.name)
        .filter((name): name is string => Boolean(name));
      return names.length > 0 ? names.join(', ') : '—';
    },
    formatLastLogin: (user) => formatLastLogin(user.last_login_at),
    canDelete: (user) => user.id !== myId,
  };

  // --- Filters -----------------------------------------------------------

  const applyFilters = (next: UserFilters) => {
    setFilters(next);
    setPage(0);
  };

  const submitSearch = () => {
    const search = searchDraft.trim();
    if (search !== filters.search) applyFilters({ ...filters, search });
  };

  const onSearchKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      submitSearch();
    }
  };

  const hasFilters =
    Boolean(filters.search) || Boolean(filters.service) || Boolean(filters.role);

  const activeChips: Array<{ key: keyof UserFilters; label: string }> = [];
  if (filters.service) {
    activeChips.push({
      key: 'service',
      label: `Service : ${
        serviceOptions.find((o) => o.value === filters.service)?.label ??
        filters.service
      }`,
    });
  }
  if (filters.role) {
    activeChips.push({
      key: 'role',
      label: `Rôle : ${
        ROLE_OPTIONS.find((o) => o.value === filters.role)?.label ?? filters.role
      }`,
    });
  }

  const clearChip = (key: keyof UserFilters) => {
    applyFilters({ ...filters, [key]: EMPTY_USER_FILTERS[key] });
  };

  const openSheet = () => {
    setSheetDraft(filters);
    setSheetOpen(true);
  };

  const applySheet = () => {
    applyFilters({ ...sheetDraft, search: filters.search });
    setSheetOpen(false);
  };

  const clearSheet = () => {
    setSheetDraft({ ...EMPTY_USER_FILTERS, search: filters.search });
  };

  // --- Actions -----------------------------------------------------------

  const onAction = (action: UserAction, user: UserRow) => {
    switch (action) {
      case 'edit':
        navigate(`/edit-user/${user.id}`);
        break;
      case 'delete':
        setToBeDeleted(user);
        break;
      default:
        break;
    }
  };

  const afterChange = () => {
    queryClient.invalidateQueries([HOME_USERS_KEY]);
    refetch();
  };

  const confirmDelete = async () => {
    if (!toBeDeleted) return;

    try {
      const response = await deleteUser(toBeDeleted.id);

      if (response.ok) {
        setNotice({ severity: 'success', text: 'Utilisateur supprimé.' });
        queryClient.invalidateQueries([HOME_USERS_KEY]);
        // Last row of a page that is not the first: step back, or the
        // page would be left empty.
        if (users.length === 1 && page > 0) {
          setPage(page - 1);
        } else {
          refetch();
        }
      } else if (response.status === 403) {
        setNotice({
          severity: 'error',
          text: "Vous n'avez pas les droits pour cette action.",
        });
      } else {
        setNotice({ severity: 'error', text: GENERIC_ERROR });
      }
    } catch (_) {
      setNotice({ severity: 'error', text: GENERIC_ERROR });
    }

    setToBeDeleted(undefined);
  };

  const closeDialog = () => navigate('/users-list');

  const onSaved = (saved: UserFormNotice) => {
    setNotice(saved);
    afterChange();
    closeDialog();
  };

  // --- Render ------------------------------------------------------------

  const searchField = (
    <Input
      placeholder="Qui cherchez-vous ?"
      variant="outlined"
      size="small"
      value={searchDraft}
      onChange={(e) => setSearchDraft(e.target.value)}
      onKeyDown={onSearchKeyDown}
      inputProps={{ 'aria-label': 'Recherche' }}
      sx={{ ...(FIELD_SX as object), flex: '1 1 260px', minWidth: 0 }}
      slotProps={{
        input: {
          endAdornment: (
            <InputAdornment position="end">
              <IconButton
                aria-label="Rechercher"
                edge="end"
                onClick={submitSearch}
                sx={{ color: colors.accent }}
              >
                <SearchIcon />
              </IconButton>
            </InputAdornment>
          ),
        },
      }}
    />
  );

  const listProps = {
    users,
    isLoading,
    isError,
    hasFilters,
    resolvers,
    onAction,
    onRetry: () => refetch(),
  };

  return (
    // Tiles hidden while a modal route is open over the list: the XD modal
    // artboards show the header over a page without tiles.
    <Container showTiles={!dialog}>
      <Box>
        {/* Filter row */}
        {isMobile ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            <Box sx={{ display: 'flex', gap: 1 }}>
              {searchField}
              <Badge
                badgeContent={activeChips.length}
                color="secondary"
                overlap="rectangular"
              >
                <Button
                  variant="outlined"
                  color="primary"
                  startIcon={<FilterListIcon />}
                  onClick={openSheet}
                  sx={{ px: 2, py: 0.75, whiteSpace: 'nowrap' }}
                >
                  Filtres
                </Button>
              </Badge>
            </Box>
            {activeChips.length > 0 && (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {activeChips.map((chip) => (
                  <Chip
                    key={chip.key}
                    label={chip.label}
                    variant="outlined"
                    onDelete={() => clearChip(chip.key)}
                  />
                ))}
              </Box>
            )}
          </Box>
        ) : (
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'center',
              gap: 1.5,
            }}
          >
            {searchField}
            <UserFilterFields
              value={filters}
              onChange={(patch) => applyFilters({ ...filters, ...patch })}
              serviceOptions={serviceOptions}
            />
          </Box>
        )}

        {/* Progress when changing page or filter, without emptying the table */}
        <Box sx={{ height: 4, mt: 2 }}>
          {isFetching && !isLoading && (
            <LinearProgress color="secondary" sx={{ height: 2 }} />
          )}
        </Box>

        {isMobile ? <UserCards {...listProps} /> : <UsersTable {...listProps} />}

        <ListPagination
          count={total}
          page={page}
          pageSize={pageSize}
          onPageChange={setPage}
          onPageSizeChange={(size) => {
            setPageSize(size);
            setPage(0);
          }}
        />
      </Box>

      {/* Bottom filter sheet (mobile) */}
      <Drawer
        anchor="bottom"
        open={sheetOpen}
        onClose={() => setSheetOpen(false)}
        PaperProps={{
          sx: {
            borderTopLeftRadius: `${radii.card * 2}px`,
            borderTopRightRadius: `${radii.card * 2}px`,
            backgroundColor: colors.white,
            maxHeight: '85vh',
          },
        }}
      >
        <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Typography
              component="h2"
              sx={{ fontSize: '1.125rem', fontWeight: fontWeights.semiBold }}
            >
              Filtres
            </Typography>
            <IconButton
              aria-label="Fermer"
              onClick={() => setSheetOpen(false)}
              sx={{ color: colors.textBase }}
            >
              <CloseIcon />
            </IconButton>
          </Box>

          <UserFilterFields
            value={sheetDraft}
            onChange={(patch) => setSheetDraft({ ...sheetDraft, ...patch })}
            serviceOptions={serviceOptions}
            stacked
          />

          <Box sx={{ display: 'flex', gap: 1.5, mt: 1 }}>
            <Button
              variant="outlined"
              color="primary"
              onClick={clearSheet}
              sx={{ flex: 1 }}
            >
              Effacer
            </Button>
            <Button
              variant="contained"
              color="secondary"
              onClick={applySheet}
              sx={{ flex: 1 }}
            >
              Appliquer
            </Button>
          </Box>
        </Box>
      </Drawer>

      {/* Screen 9: create, and the same modal to modify */}
      <UserFormDialog
        open={dialog === 'create'}
        mode="create"
        onClose={closeDialog}
        onSaved={onSaved}
      />
      <UserFormDialog
        open={dialog === 'edit'}
        mode="edit"
        userId={editedId}
        onClose={closeDialog}
        onSaved={onSaved}
      />
      <UserFormDialog
        open={dialog === 'view'}
        mode="view"
        userId={editedId}
        onClose={closeDialog}
        onSaved={onSaved}
        onMissing={() => navigate('/404')}
      />

      {/* Delete confirmation, with the XD modal pattern */}
      <Dialog
        className={BRAND_DIALOG_CLASS}
        open={Boolean(toBeDeleted)}
        onClose={() => !isDeleting && setToBeDeleted(undefined)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Supprimer l'utilisateur</DialogTitle>
        <DialogContent dividers>
          <DialogContentText>
            Êtes-vous sûr de vouloir supprimer l'utilisateur «{' '}
            {toBeDeleted ? displayName(toBeDeleted) : ''} » ? Cette action est
            irréversible.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button
            variant="outlined"
            color="inherit"
            onClick={() => setToBeDeleted(undefined)}
            disabled={isDeleting}
          >
            Annuler
          </Button>
          <Button
            variant="contained"
            color="secondary"
            onClick={confirmDelete}
            disabled={isDeleting}
            autoFocus
          >
            Supprimer
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        open={Boolean(notice)}
        autoHideDuration={6000}
        disableWindowBlurListener
        onClose={() => setNotice(undefined)}
      >
        <Alert
          severity={notice?.severity ?? 'success'}
          variant="filled"
          onClose={() => setNotice(undefined)}
          sx={{ width: '100%' }}
        >
          {notice?.text}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default UsersList;
